import { PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { Private_Routes } from '@common/url';
import { DotsHorizontalIcon, TrashCIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Badges, Button, Display, Dropdown, Stack, Text } from '@prophecy/ui-v3';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { InfoCardType, StatusMapKeys } from '../../../../data/apis/transpileTypes';
import { useDeleteTranspiler } from '../../components//DeleteTranspiler';
import { FileFormats } from '../../config';
import { ImportStepValue } from '../../Import/steps/types';
import { NotAssigned } from '../util';

export const InfoHeader = ({
  infoData,
  transpilerId,
  isBulk
}: {
  infoData?: InfoCardType;
  transpilerId: string;
  isBulk?: boolean;
}) => {
  const { remove, mutation } = useDeleteTranspiler(transpilerId, infoData?.name as string, isBulk);
  const navigate = useNavigate();
  const {
    meteringInfo: { plan }
  } = useProfessionalEditionCreditsContext();
  const isProfessionalEdition = plan === PLAN_TYPES.Pro;

  const teamName = infoData?.teamName;
  const isTranspiled =
    isCaseInsensitiveEqual(infoData?.status, StatusMapKeys.completed) ||
    isCaseInsensitiveEqual(infoData?.status, StatusMapKeys.completed);
  const isCancelling = isCaseInsensitiveEqual(infoData?.status, StatusMapKeys.cancelling);
  const teamId = infoData?.teamId;
  const teamUI = (
    <Text level='xs' tone={theme.colors.brand600} weight={theme.fontWeight.medium}>
      {teamName || 'Team'}
    </Text>
  );

  const handleDelete = async () => {
    const isDelete = await remove();
    if (isDelete) {
      navigate(Private_Routes.Transpiler.getUrl());
    }
  };

  const overlay = (
    <>
      <Dropdown.Item
        onClick={() => (mutation.isLoading ? null : handleDelete())}
        data-test-id='info-header-delete-dropdown-item'>
        <Stack direction='horizontal' align='center' gap={theme.spaces.x8}>
          <TrashCIcon type='default' size='s' /> Delete
        </Stack>
      </Dropdown.Item>
    </>
  );

  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} align='space-between'>
      <Stack gap={theme.spaces.x12}>
        <Stack direction='horizontal' gap={theme.spaces.x10} width='100%' alignY='center'>
          <Display level='xs' weight={theme.fontWeight.medium} color={theme.colors.gray900}>
            {infoData?.name || 'Bundle Detail'}
          </Display>
          {infoData?.projectReleaseVersion && (
            <Badges tone='gray' size='s'>
              {infoData?.projectReleaseVersion}
            </Badges>
          )}
        </Stack>

        <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
          <Text level='xs' tone={theme.colors.gray500}>
            by
          </Text>
          {teamId && teamName !== NotAssigned ? (
            <Button
              variant='plain'
              elementType='link'
              to={Private_Routes.Team.home.getUrl({ uid: teamId })}
              data-test-id='info-header-team-link-button'>
              {teamUI}
            </Button>
          ) : (
            teamUI
          )}
        </Stack>
      </Stack>
      <Stack direction='horizontal' gap={theme.spaces.x12}>
        {isBulk && !isCancelling && !isProfessionalEdition && (
          <Button
            elementType='link'
            to={Private_Routes.TranspilerImport.tab.getUrl(
              { transpileId: transpilerId },
              {
                source: infoData?.source || FileFormats.abinitio,
                step: `${isTranspiled ? ImportStepValue.TranspileGraph : ImportStepValue.Sources}`
              }
            )}
            data-test-id='info-header-reimport-button'>
            Re-import
          </Button>
        )}
        <Dropdown placement='bottom' align='end' overlay={overlay} data-test-id='transpiler-info-header-dropdown'>
          <Button
            variant='secondaryGray'
            icon={<DotsHorizontalIcon type='default' />}
            data-test-id='info-header-dropdown-button'
            aria-label='Info options menu'
          />
        </Dropdown>
      </Stack>
    </Stack>
  );
};
